<template>
  <div class="profile-page">
    <div class="wrapper">
      <div class="profile-header">
        <div class="profile-info">
          <div class="profile-avatar">
            <img src="@/assets/img/default-profile-logo.svg" alt="" srcset="" />
          </div>
          <div class="profile-details">
            <h2>{{ fullName }}</h2>
            <p>{{ user?.email }}</p>
          </div>
        </div>
        <div class="profile-actions">
          <BaseButton
            :text="$t('WITHDRAW')"
            buttonClass="modal-button"
            wrapperClass="edit-profile-button-wrapper"
            cornerColor="#15CA5C"
            @click="openWithdraw()"
          />
          <BaseButton
            :text="$t('EDIT PROFILE')"
            @click="changeTab('personal-information')"
            buttonClass="modal-button"
            wrapperClass="edit-profile-button-wrapper"
            cornerColor="#15CA5C"
          />
        </div>
      </div>
      <div class="tabs-and-content_container">
        <div class="user-info">
          <div class="title extrasmall">
            {{ $t("My Profile") }}
          </div>
          <div class="info">
            <div class="info-icon">
              <img src="@/assets/img/user.svg" />
            </div>
            <div class="info-text">
              <div class="desc">
                <b>{{ user.name }} {{ user.surname }}</b>
              </div>
              <div class="desc small text-red">
                <b>{{ user.email }}</b>
              </div>
            </div>
          </div>
          <button class="button" @click="logout()">
            {{ $t("LOG OUT") }}
          </button>
        </div>
        <div class="tabs-and-content">
          <!-- Tab Navigation -->
          <div class="profile-tabs">
            <div
              v-for="tab in tabs"
              :key="tab.key"
              :class="['tab', { active: activeTab === tab.key }]"
              @click="changeTab(tab.key)"
            >
              {{ tab.label }}
            </div>
          </div>

          <!-- Dynamic Tab Content -->
          <component
            :is="currentTabComponent"
            :user="user"
            :orderHistory="orderHistory"
            :balance="balance"
            :countryOptions="countryOptions"
            :minTopupAmount="minTopupAmount"
            @updatePersonalInfo="updatePersonalInfo"
            @updateBillingInfo="updateBillingInfo"
            @updateProfile="updateProfile"
            @updatePassword="updatePassword"
            @openWithdraw="openWithdraw"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MyBalance from "./tabs/MyBalance.vue";
import OrderHistory from "./tabs/OrderHistory.vue";
import OfferHistory from "./tabs/OfferHistory.vue";
import ChangePassword from "./tabs/ChangePassword.vue";
import PersonalInformation from "./tabs/PersonalInformation.vue";

export default {
  name: "ProfilePage",

  components: {
    MyBalance,
    OrderHistory,
    OfferHistory,
    ChangePassword,
    PersonalInformation,
  },

  props: {
    isAuth: {
      type: Boolean,
      required: true,
    },
    orderHistory: {
      type: Array,
      required: true,
    },
    minTopupAmount: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      tabs: [
        { key: "personal-information", label: this.$t("Settings") },
        { key: "my-balance", label: this.$t("Transactions") },
        { key: "order-history", label: this.$t("Order History") },
      ],
      activeTab: "my-balance",
      user: {
        email: "",
        name: "",
        surname: "",
        phone: "",
        phoneCountryCode: null,
        address: "",
        country: "",
        city: "",
        zip: "",
        balance: 0,
        steamId: "",
        steamProfileUrl: null,
        avatarMedium: "",
        status: "",
      },
      countryOptions: [],
    };
  },
  computed: {
    fullName() {
      return `${this?.user?.name} ${this?.user?.surname}`.trim();
    },
    currentTabComponent() {
      switch (this.activeTab) {
        case "my-balance":
          return "MyBalance";
        case "transaction":
          return "TransactionHistory";
        case "order-history":
          return "OrderHistory";
        case "offer-history":
          return "OfferHistory";
        case "change-password":
          return "ChangePassword";
        case "personal-information":
          return "PersonalInformation";
        default:
          return "MyBalance";
      }
    },
  },
  watch: {
    "$route.params.page"(newPage) {
      if (newPage && this.tabs.some((tab) => tab.key === newPage)) {
        this.activeTab = newPage;
      } else {
        this.activeTab = "my-balance";
      }
    },
  },
  methods: {
    logout() {
      this.$emit("logout");
    },
    back() {
      this.$router.go(-1);
    },
    openWithdraw() {
      this.$emit("openWithdraw");
    },
    changeTab(tabKey) {
      this.activeTab = tabKey;
      this.$router.push({ name: "ProfilePage", params: { page: tabKey } });
    },

    async fetchUserData() {
      try {
        const response = await this.$http.get(
          process.env.VUE_APP_API + "user/profile",
        );
        if (response.status === 200 && response.data) {
          this.user.name = response.data.name;
          this.user.surname = response.data.surname;
          this.user.email = response.data.email;
          this.user.phone = response.data.phone;
          this.user.country = response.data.country;
          this.user.city = response.data.city;
          this.user.zip = response.data.zip;
          this.user.address = response.data.address;
          this.user.balance = response.data.balance;
          this.user.steamId = response.data.steamId;
          this.user.steamProfileUrl = response.data.steam_profile_url;
          this.user.avatarMedium = response.data.avatar_medium;
          this.user.status = response.data.status;
        }
      } catch (error) {
        this.$router.push({ path: "/" });
        console.error("Error fetching user data:", error);
      }
    },

    async updatePersonalInfo(data) {
      try {
        await this.$http.put(process.env.VUE_APP_API + "user/profile", data);
        this.personalInfo = data;
      } catch (error) {
        console.error("Error updating personal information:", error);
      }
    },

    async updateBillingInfo(data) {
      try {
        await this.$http.put(process.env.VUE_APP_API + "user/profile", data);
        this.billingInfo = data;
      } catch (error) {
        console.error("Error updating billing information:", error);
      }
    },

    async updateProfile(data) {
      try {
        await this.$http.put(process.env.VUE_APP_API + "user/profile", data);
        this.personalInfo = {
          firstName: data.name,
          lastName: data.surname,
          email: data.email,
          phone: data.phone,
        };
        this.billingInfo = {
          country: data.country,
          city: data.city,
          zipCode: data.zip,
          address: data.address,
        };
      } catch (error) {
        console.error("Error updating profile:", error);
      }
    },

    async updatePassword(passwordData) {
      try {
        const data = {
          oldPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
          confirmPassword: passwordData.confirmPassword,
        };
        await this.$http.post(
          process.env.VUE_APP_API + "user/change-password",
          data,
        );

        this.$message({
          message: "Password updated successfully!",
          type: "success",
        });
      } catch (error) {
        console.error("Error updating password:", error);

        this.$message({
          message: "Error updating password. Please try again.",
          type: "error",
        });
      }
    },

    async fetchCountryOptions() {
      try {
        const response = await this.$http.get(
          process.env.VUE_APP_API + "countries",
        );
        this.countryOptions = response.data.payload;
      } catch (error) {
        console.error("Error fetching country options:", error);
      }
    },
  },

  created() {
    const page = this.$route.params.page;
    if (page && this.tabs.some((tab) => tab.key === page)) {
      this.activeTab = page;
    }

    this.fetchUserData();
    this.fetchCountryOptions();
  },
};
</script>

<style lang="scss" scoped>
.tabs-and-content_container {
  gap: 16px;
  display: flex;
  align-items: flex-start;
}
.tabs-and-content {
  flex: 1;
}
.user-info {
  flex: 1;
  max-width: 450px;
  background: #191919;
  border-radius: 12px;
  padding: 30px;

  .info {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-top: 32px;

    .desc + .desc {
      margin-top: 4px;
    }
  }
  .button {
    margin-top: 30px;
  }
}
.profile-title {
  margin: 32px 0;
  display: flex;
  align-items: center;
  gap: 10px;
}
.profile-page {
  width: 100%;
  padding: 32px 16px 0;
}

.profile-page > .wrapper {
  max-width: 1360px;

  margin: 0 auto;
}

.profile-header-wrapper {
  border: 1px solid #191919;
  padding: 12px;
  margin-bottom: 34px;
  display: none;
}

.profile-header {
  background-color: #191919;
  padding: 51px 54px 51px 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  display: none;
}

.profile-info {
  display: flex;
  align-items: center;
  gap: 34px;
}

.profile-avatar {
  width: 42px;
  height: 42px;
  min-width: 42px;
  min-height: 42px;
  background: #15ca5c1a;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    width: 18px;
    height: 18px;
  }
}

.profile-details h2 {
  font-family: "Roboto", sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: white;
  margin-bottom: 7px;
  max-width: 110px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-details p {
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  color: #b7b8c1;
  max-width: 110px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-actions {
  display: flex;
  align-items: center;
  width: 100%;
  /* or a specific width */
  justify-content: flex-end;
}

.wrapper.edit-profile-button-wrapper {
  margin: 0 34px 0 0;
}

.logout-text {
  font-family: "Roboto", sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: white;
  margin-right: 16px;
  cursor: pointer;
}

.logout-icon {
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.profile-tabs {
  display: flex;
  border-radius: 12px;
  gap: 10px;
}

.tab {
  padding: 16px 20px;
  font-family: "Roboto", sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  background: #191919;
  cursor: pointer;
  position: relative;
  text-align: center;
  border-radius: 12px;
  max-width: 194px;
  flex: 1;
}

.tab.active {
  background: #9d1616;
  color: #fff;
}

.bottom-light {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
  pointer-events: none;
  left: 50%;
  transform: translateX(-50%);
}

.top-block {
  position: relative;

  .title-top {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
  }
  .main-title {
    width: 100%;
    max-width: 1420px;
    margin: 52px auto;
    display: flex;
    align-items: center;
    gap: 10px;

    button {
      line-height: 0;
    }
  }

  .cs2-title {
    font-size: 16px;
  }
}

@media (max-width: 1200px) {
  .profile-page {
    padding: 0 20px;
  }

  .profile-tabs {
    overflow-x: auto;
  }

  .tab {
    white-space: nowrap;
  }
}

@media (max-width: 768px) {
  .tabs-and-content_container {
    flex-direction: column;
  }
  .user-info {
    width: 100%;
    max-width: 100%;
  }
  .profile-header {
    flex-direction: column;
    align-items: center;
    padding: 20px;
  }

  .profile-actions {
    justify-content: center;
    margin-top: 20px;
  }

  .top-block .title-top {
    position: static;
    transform: none;
  }
  .top-block .main-title {
    margin: 24px auto;
  }
  .tabs-and-content {
    margin-top: 0;
    width: 100%;
  }
}
</style>
